import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { ProduccionB2bShell } from "@/components/b2b/ProduccionB2bShell";
import { B2bRulesPanel } from "@/components/settings/MarketSettingsPanels";
import { PageLoader } from "@/components/PageLoader";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useTenantContext } from "@/lib/tenant-context";
import { toast } from "sonner";

export const Route = createFileRoute("/app/produccion/reglas")({
  component: ProduccionReglasPage,
  head: () => ({ meta: [{ title: "Reglas de pedidos — B2B / Mayorista" }] }),
});

function ProduccionReglasPage() {
  const tid = useWorkspaceTenantId();
  const navigate = useNavigate();
  const { businessProfile } = useTenantContext();
  const [deadlineHour, setDeadlineHour] = useState("10");
  const [cutoffHours, setCutoffHours] = useState("24");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!tid) return;
    setLoading(true);
    try {
      const cRes = await apiGetJson<{ config: Record<string, unknown> | null }>(
        "/api/tenant/config",
        tid,
      );
      const c = cRes.config;
      setDeadlineHour(String(c?.institutionalOrderDeadlineHour ?? 10));
      setCutoffHours(String(c?.institutionalCutoffHours ?? 24));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al cargar reglas");
    } finally {
      setLoading(false);
    }
  }, [tid]);

  useEffect(() => {
    void load();
  }, [load]);

  const goBack = () => {
    void navigate({ to: "/app/produccion", search: {} as never });
  };

  const onSave = async () => {
    if (!tid) return;
    setBusy(true);
    try {
      await apiSendJson(
        "/api/tenant/config",
        "PATCH",
        {
          institutional_order_deadline_hour: Number(deadlineHour) || 10,
          institutional_cutoff_hours: Number(cutoffHours) || 24,
        },
        tid,
      );
      toast.success("Reglas guardadas");
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al guardar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ProduccionB2bShell title="Reglas de pedidos" subtitle="Cierre diario y anticipación de pedidos mayoristas">
      {!tid ? (
        <p className="text-sm text-muted-foreground">Seleccioná un workspace.</p>
      ) : loading ? (
        <PageLoader label="Cargando reglas…" />
      ) : (
        <B2bRulesPanel
          deadlineHour={deadlineHour}
          setDeadlineHour={setDeadlineHour}
          cutoffHours={cutoffHours}
          setCutoffHours={setCutoffHours}
          busy={busy}
          onSave={() => void onSave()}
          onCancel={goBack}
          businessProfile={businessProfile}
        />
      )}
    </ProduccionB2bShell>
  );
}
